const splitByPipe = (value) =>
  String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);

const getCurrentLanguage = () =>
  document.body.classList.contains("set-lang-de") ? "de" : "en";

const getLocalizedList = (lang, localizedLists, fallbackList) => {
  const normalized = lang === "en" ? "en" : "de";
  const localized = Array.isArray(localizedLists?.[normalized]) ? localizedLists[normalized] : [];
  if (localized.length) return localized;
  return Array.isArray(fallbackList) ? fallbackList : [];
};

const wrapIndex = (index, size) => {
  if (size <= 0) return 0;
  return (index % size + size) % size;
};

const SVG_NS = "http://www.w3.org/2000/svg";
const SLIDE_RESPONSIVE_WIDTHS = [640, 960, 1200];
const SLIDE_RESPONSIVE_SIZES = "(max-width: 767px) 92vw, (max-width: 1199px) 86vw, 686px";
const SLIDE_IMAGE_EXT_RE = /\.(avif|webp|jpe?g|png)(?:\?.*)?$/i;
const SRC_RAW_SEGMENT = "/" + "src_raw" + "/";

const deriveResponsiveVariants = (src) => {
  const cleaned = String(src || "").trim();
  if (!cleaned) {
    return { fallback: "", avif: "", webp: "" };
  }
  if (
    cleaned.includes(SRC_RAW_SEGMENT) ||
    cleaned.includes(".." + SRC_RAW_SEGMENT)
  ) {
    return { fallback: cleaned, avif: "", webp: "" };
  }
  const stem = cleaned.replace(SLIDE_IMAGE_EXT_RE, "");
  if (!stem || stem === cleaned) {
    return { fallback: cleaned, avif: "", webp: "" };
  }
  return {
    fallback: cleaned,
    avif: SLIDE_RESPONSIVE_WIDTHS.map((width) => `${stem}-${width}.avif ${width}w`).join(", "),
    webp: SLIDE_RESPONSIVE_WIDTHS.map((width) => `${stem}-${width}.webp ${width}w`).join(", "),
  };
};

const buildResponsivePicture = (src, alt, imageClass, loading = "lazy") => {
  const variants = deriveResponsiveVariants(src);
  const picture = document.createElement("picture");
  picture.className = "wirkung-stack-picture";
  if (variants.avif) {
    const sourceAvif = document.createElement("source");
    sourceAvif.type = "image/avif";
    sourceAvif.srcset = variants.avif;
    picture.appendChild(sourceAvif);
  }
  if (variants.webp) {
    const sourceWebp = document.createElement("source");
    sourceWebp.type = "image/webp";
    sourceWebp.srcset = variants.webp;
    picture.appendChild(sourceWebp);
  }

  const image = document.createElement("img");
  image.className = imageClass;
  image.src = variants.fallback || src;
  image.alt = alt;
  image.loading = loading;
  image.decoding = "async";
  image.sizes = SLIDE_RESPONSIVE_SIZES;
  picture.appendChild(image);

  return { picture, image };
};

const ensureMainSources = (mainImage) => {
  const picture = mainImage?.parentElement?.tagName?.toLowerCase() === "picture" ? mainImage.parentElement : null;
  if (!picture) return { avif: null, webp: null };

  let avif = picture.querySelector("[data-wirkung-slide-source-avif]") || picture.querySelector('source[type="image/avif"]');
  let webp = picture.querySelector("[data-wirkung-slide-source-webp]") || picture.querySelector('source[type="image/webp"]');

  if (!avif) {
    avif = document.createElement("source");
    avif.type = "image/avif";
    picture.insertBefore(avif, mainImage);
  }
  if (!webp) {
    webp = document.createElement("source");
    webp.type = "image/webp";
    picture.insertBefore(webp, mainImage);
  }

  avif.setAttribute("data-wirkung-slide-source-avif", "");
  webp.setAttribute("data-wirkung-slide-source-webp", "");

  return { avif, webp };
};

const createIcon = (type) => {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.classList.add("wirkung-icon-svg");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.35");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");

  if (type === "left") {
    path.setAttribute("d", "M14.25 5.75L7.75 12l6.5 6.25");
    svg.appendChild(path);
    return svg;
  }

  if (type === "right") {
    path.setAttribute("d", "M9.75 5.75L16.25 12l-6.5 6.25");
    svg.appendChild(path);
    return svg;
  }

  path.setAttribute("d", "M6.75 6.75L17.25 17.25M17.25 6.75L6.75 17.25");
  svg.appendChild(path);
  return svg;
};

const applySvgButtons = (buttons) => {
  buttons.forEach(({ element, icon }) => {
    if (!element) return;
    if (element.dataset.wirkungSvgReady === "true") return;
    element.replaceChildren(createIcon(icon));
    element.dataset.wirkungSvgReady = "true";
  });
};

const buildRuntimeSlider = (sources, alts, sliderIndex = -1) => {
  const safeSources = Array.isArray(sources) ? sources.filter(Boolean) : [];
  if (!safeSources.length) return null;
  const safeAlts = Array.isArray(alts) ? alts : [];
  const slider = {
    root: null,
    sources: safeSources,
    alts: safeAlts,
    titles: [],
    descriptions: [],
    mainImage: null,
    mainLabel: null,
    mainSourceAvif: null,
    mainSourceWebp: null,
    openButton: null,
    prevButton: null,
    nextButton: null,
    currentIndex: 0,
    sliderIndex,
  };

  slider.updateMainImage = () => {
    const safeIndex = wrapIndex(slider.currentIndex, slider.sources.length);
    const src = slider.sources[safeIndex];
    const fallback = `Image ${safeIndex + 1}`;
    const alt = slider.alts[safeIndex] || fallback;
    if (!src) return;
    if (slider.mainImage) {
      const variants = deriveResponsiveVariants(src);
      if (slider.mainSourceAvif) {
        if (variants.avif) slider.mainSourceAvif.srcset = variants.avif;
        else slider.mainSourceAvif.removeAttribute("srcset");
      }
      if (slider.mainSourceWebp) {
        if (variants.webp) slider.mainSourceWebp.srcset = variants.webp;
        else slider.mainSourceWebp.removeAttribute("srcset");
      }
      slider.mainImage.src = variants.fallback || src;
      slider.mainImage.alt = alt;
      if (!slider.mainImage.sizes) slider.mainImage.sizes = SLIDE_RESPONSIVE_SIZES;
    }
    if (slider.mainLabel) {
      const label = String(slider.titles[safeIndex] || "").trim();
      slider.mainLabel.textContent = label;
      slider.mainLabel.classList.toggle("is-hidden", !label);
    }
  };

  slider.showImage = (nextIndex) => {
    slider.currentIndex = wrapIndex(nextIndex, slider.sources.length);
    slider.updateMainImage();
  };

  return slider;
};

const buildSlider = (root, sliderIndex) => {
  const defaultSources = splitByPipe(root.dataset.wirkungImages);
  const localizedSources = {
    de: splitByPipe(root.dataset.wirkungImagesDe),
    en: splitByPipe(root.dataset.wirkungImagesEn),
  };
  const sources = getLocalizedList(getCurrentLanguage(), localizedSources, defaultSources);
  if (!sources.length) return null;

  const defaultAlts = splitByPipe(root.dataset.wirkungAlts);
  const localizedAlts = {
    de: splitByPipe(root.dataset.wirkungAltsDe),
    en: splitByPipe(root.dataset.wirkungAltsEn),
  };
  const alts = getLocalizedList(getCurrentLanguage(), localizedAlts, defaultAlts);
  const defaultTitles = splitByPipe(root.dataset.wirkungTitles);
  const localizedTitles = {
    de: splitByPipe(root.dataset.wirkungTitlesDe),
    en: splitByPipe(root.dataset.wirkungTitlesEn),
  };
  const titles = getLocalizedList(getCurrentLanguage(), localizedTitles, defaultTitles);
  const defaultDescriptions = splitByPipe(root.dataset.wirkungDescriptions);
  const localizedDescriptions = {
    de: splitByPipe(root.dataset.wirkungDescriptionsDe),
    en: splitByPipe(root.dataset.wirkungDescriptionsEn),
  };
  const descriptions = getLocalizedList(getCurrentLanguage(), localizedDescriptions, defaultDescriptions);

  const mainImage = root.querySelector("[data-wirkung-slide-image]");
  const mainLabel = root.querySelector("[data-wirkung-slide-label]");
  const openButton = root.querySelector("[data-wirkung-open]");
  const prevButton = root.querySelector("[data-wirkung-prev]");
  const nextButton = root.querySelector("[data-wirkung-next]");
  if (!mainImage) return null;
  const mainSources = ensureMainSources(mainImage);

  const slider = buildRuntimeSlider(sources, alts, sliderIndex);
  if (!slider) return null;
  slider.root = root;
  slider.mainImage = mainImage;
  slider.mainLabel = mainLabel;
  slider.mainSourceAvif = mainSources.avif;
  slider.mainSourceWebp = mainSources.webp;
  slider.openButton = openButton;
  slider.prevButton = prevButton;
  slider.nextButton = nextButton;
  slider.titles = titles;
  slider.descriptions = descriptions;
  slider.defaultSources = defaultSources;
  slider.defaultAlts = defaultAlts;
  slider.defaultTitles = defaultTitles;
  slider.defaultDescriptions = defaultDescriptions;
  slider.localizedSources = localizedSources;
  slider.localizedAlts = localizedAlts;
  slider.localizedTitles = localizedTitles;
  slider.localizedDescriptions = localizedDescriptions;
  slider.refreshDesktopStack = null;
  slider.setLanguage = (lang) => {
    const nextSources = getLocalizedList(lang, slider.localizedSources, slider.defaultSources);
    if (!nextSources.length) return;
    const nextAlts = getLocalizedList(lang, slider.localizedAlts, slider.defaultAlts);
    const nextTitles = getLocalizedList(lang, slider.localizedTitles, slider.defaultTitles);
    const nextDescriptions = getLocalizedList(lang, slider.localizedDescriptions, slider.defaultDescriptions);
    const hasSourceChange = nextSources.join("|") !== slider.sources.join("|");
    const hasTitleChange = nextTitles.join("|") !== slider.titles.join("|");
    slider.sources = nextSources;
    slider.alts = nextAlts;
    slider.titles = nextTitles;
    slider.descriptions = nextDescriptions;
    slider.currentIndex = wrapIndex(slider.currentIndex, slider.sources.length);
    if ((hasSourceChange || hasTitleChange) && typeof slider.refreshDesktopStack === "function") {
      slider.refreshDesktopStack();
    }
    slider.updateMainImage();
  };
  return slider;
};

export function initWirkungSlideshow() {
  const sliderRoots = Array.from(document.querySelectorAll("[data-wirkung-slideshow]"));
  if (!sliderRoots.length) return;

  const sliders = sliderRoots
    .map((root, index) => buildSlider(root, index))
    .filter(Boolean);
  if (!sliders.length) return;

  const lightbox = document.querySelector("[data-wirkung-lightbox]");
  const lightboxImage = lightbox?.querySelector("[data-wirkung-lightbox-image]");
  let lightboxMeta = lightbox?.querySelector("[data-wirkung-lightbox-meta]");
  let lightboxMetaTitle = lightbox?.querySelector("[data-wirkung-lightbox-meta-title]");
  let lightboxMetaDescription = lightbox?.querySelector("[data-wirkung-lightbox-meta-description]");
  const lightboxPrev = lightbox?.querySelector("[data-wirkung-lightbox-prev]");
  const lightboxNext = lightbox?.querySelector("[data-wirkung-lightbox-next]");
  const lightboxCloseButton = lightbox?.querySelector(".wirkung-lightbox-close");
  const lightboxCloseButtons = lightbox
    ? Array.from(lightbox.querySelectorAll("[data-wirkung-close]"))
    : [];

  if (lightbox && !lightboxMeta) {
    lightboxMeta = document.createElement("div");
    lightboxMeta.className = "wirkung-lightbox-meta";
    lightboxMeta.setAttribute("data-wirkung-lightbox-meta", "");
    lightboxMetaTitle = document.createElement("p");
    lightboxMetaTitle.className = "wirkung-lightbox-meta-title";
    lightboxMetaTitle.setAttribute("data-wirkung-lightbox-meta-title", "");
    lightboxMetaDescription = document.createElement("p");
    lightboxMetaDescription.className = "wirkung-lightbox-meta-description";
    lightboxMetaDescription.setAttribute("data-wirkung-lightbox-meta-description", "");
    lightboxMeta.append(lightboxMetaTitle, lightboxMetaDescription);
    const card = lightbox.querySelector(".wirkung-lightbox-card");
    card?.appendChild(lightboxMeta);
  }

  let activeSlider = sliders[0];
  let isLightboxOpen = false;

  applySvgButtons([
    { element: lightboxPrev, icon: "left" },
    { element: lightboxNext, icon: "right" },
    { element: lightboxCloseButton, icon: "close" },
  ]);

  const updateLightboxImage = () => {
    if (!lightboxImage || !activeSlider) return;
    const safeIndex = wrapIndex(activeSlider.currentIndex, activeSlider.sources.length);
    const src = activeSlider.sources[safeIndex];
    const fallback = `Impact slide ${activeSlider.sliderIndex + 1}-${safeIndex + 1}`;
    const alt = activeSlider.alts[safeIndex] || fallback;
    const title = String(activeSlider.titles[safeIndex] || "").trim();
    const description = String(activeSlider.descriptions[safeIndex] || "").trim();
    if (!src) return;
    lightboxImage.src = src;
    lightboxImage.alt = alt;
    if (lightboxMeta && lightboxMetaTitle && lightboxMetaDescription) {
      const hasTitle = Boolean(title);
      const hasDescription = Boolean(description);
      lightboxMeta.classList.toggle("is-visible", hasTitle || hasDescription);
      lightboxMetaTitle.classList.toggle("is-visible", hasTitle);
      lightboxMetaDescription.classList.toggle("is-visible", hasDescription);
      lightboxMetaTitle.textContent = hasTitle ? title : "";
      lightboxMetaDescription.textContent = hasDescription ? description : "";
    }
  };

  const syncLightboxNavState = () => {
    const showNav = Boolean(activeSlider && activeSlider.sources.length > 1);
    [lightboxPrev, lightboxNext].forEach((button) => {
      if (!button) return;
      button.classList.toggle("is-hidden", !showNav);
      button.disabled = !showNav;
      button.setAttribute("aria-hidden", showNav ? "false" : "true");
      button.tabIndex = showNav ? 0 : -1;
    });
  };

  const syncSliderToLightbox = (slider) => {
    if (!isLightboxOpen || slider !== activeSlider) return;
    updateLightboxImage();
    syncLightboxNavState();
  };

  const openLightbox = (slider) => {
    if (!lightbox) return;
    activeSlider = slider;
    isLightboxOpen = true;
    lightbox.classList.add("is-open");
    lightbox.setAttribute("aria-hidden", "false");
    document.body.classList.add("wirkung-lightbox-open");
    updateLightboxImage();
    syncLightboxNavState();
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    isLightboxOpen = false;
    lightbox.classList.remove("is-open");
    lightbox.setAttribute("aria-hidden", "true");
    document.body.classList.remove("wirkung-lightbox-open");
  };

  const renderDesktopStack = (slider) => {
    const existingStack = slider.root.querySelector("[data-wirkung-slide-stack]");
    if (existingStack) existingStack.remove();

    const stack = document.createElement("div");
    stack.className = "wirkung-slide-stack";
    stack.setAttribute("data-wirkung-slide-stack", "");

    slider.sources.forEach((src, index) => {
      const fallback = `Impact slide ${slider.sliderIndex + 1}-${index + 1}`;
      const alt = slider.alts[index] || fallback;

      const itemButton = document.createElement("button");
      itemButton.type = "button";
      itemButton.className = "wirkung-stack-item";
      itemButton.setAttribute("aria-label", `Show image ${index + 1} large`);

      const { picture } = buildResponsivePicture(src, alt, "wirkung-stack-image");
      itemButton.appendChild(picture);
      const label = String(slider.titles[index] || "").trim();
      const labelNode = document.createElement("span");
      labelNode.className = "wirkung-stack-label";
      labelNode.textContent = label;
      labelNode.classList.toggle("is-hidden", !label);
      itemButton.appendChild(labelNode);
      itemButton.addEventListener("click", (event) => {
        event.preventDefault();
        slider.showImage(index);
        openLightbox(slider);
      });

      stack.appendChild(itemButton);
    });

    slider.root.appendChild(stack);
  };

  sliders.forEach((slider) => {
    applySvgButtons([
      { element: slider.prevButton, icon: "left" },
      { element: slider.nextButton, icon: "right" },
    ]);

    slider.prevButton?.addEventListener("click", (event) => {
      event.preventDefault();
      slider.showImage(slider.currentIndex - 1);
      syncSliderToLightbox(slider);
    });

    slider.nextButton?.addEventListener("click", (event) => {
      event.preventDefault();
      slider.showImage(slider.currentIndex + 1);
      syncSliderToLightbox(slider);
    });

    slider.openButton?.addEventListener("click", (event) => {
      event.preventDefault();
      openLightbox(slider);
    });

    slider.refreshDesktopStack = () => {
      renderDesktopStack(slider);
    };
    slider.refreshDesktopStack();
    slider.showImage(0);
  });

  document.addEventListener("language:changed", (event) => {
    const requested = String(event?.detail?.language || "").trim().toLowerCase();
    const lang = requested === "en" ? "en" : "de";
    sliders.forEach((slider) => {
      slider.setLanguage?.(lang);
      syncSliderToLightbox(slider);
    });
  });

  lightboxPrev?.addEventListener("click", (event) => {
    event.preventDefault();
    if (!activeSlider) return;
    activeSlider.showImage(activeSlider.currentIndex - 1);
    updateLightboxImage();
  });

  lightboxNext?.addEventListener("click", (event) => {
    event.preventDefault();
    if (!activeSlider) return;
    activeSlider.showImage(activeSlider.currentIndex + 1);
    updateLightboxImage();
  });

  document.addEventListener("wirkung:open-lightbox", (event) => {
    const detail = event?.detail || {};
    const externalImages = Array.isArray(detail.images) ? detail.images : [];
    const sources = externalImages
      .map((item) => String(item?.src || "").trim())
      .filter(Boolean);
    if (!sources.length) return;
    const alts = externalImages.map((item, index) => {
      const alt = String(item?.alt || "").trim();
      return alt || `Image ${index + 1}`;
    });
    const slider = buildRuntimeSlider(sources, alts, -1);
    if (!slider) return;
    slider.titles = externalImages.map((item) => String(item?.title || "").trim());
    slider.descriptions = externalImages.map((item) => String(item?.description || "").trim());
    const requestedStart = Number(detail.startIndex);
    const startIndex = Number.isFinite(requestedStart) ? requestedStart : 0;
    slider.showImage(startIndex);
    openLightbox(slider);
  });

  lightboxCloseButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      closeLightbox();
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isLightboxOpen) {
      event.preventDefault();
      closeLightbox();
      return;
    }

    const focusedSlider = sliders.find((slider) => slider.root.contains(document.activeElement));
    const keyboardSlider = isLightboxOpen ? activeSlider : focusedSlider;
    if (!keyboardSlider) return;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      keyboardSlider.showImage(keyboardSlider.currentIndex - 1);
      syncSliderToLightbox(keyboardSlider);
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      keyboardSlider.showImage(keyboardSlider.currentIndex + 1);
      syncSliderToLightbox(keyboardSlider);
    }
  });
}
